// Browser geolocation for "cheapest near you", with the last-known position
// persisted in local storage so returning users aren't re-prompted every visit.
// This is the ONLY location path — if the user declines or geolocation is
// unavailable, results fall back to national prices (no city picker).

const STORAGE_KEY = 'geoLocation'
// Set when the user clears the location. The position itself expires after 24h,
// so without a separate flag a granted permission would silently re-acquire on
// the next visit and "Poništi" would look broken.
const OPT_OUT_KEY = 'geoOptOut'
// A saved position expires so we don't silently claim "prices near you" from a
// weeks-old location (e.g. scanned in Zagreb, now in Split).
const MAX_AGE_MS = 24 * 60 * 60 * 1000

export function getSavedLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const loc = JSON.parse(raw)
    if (typeof loc?.lat !== 'number' || typeof loc?.lon !== 'number') return null
    if (loc.ts && Date.now() - loc.ts > MAX_AGE_MS) return null // stale → re-prompt
    return { lat: loc.lat, lon: loc.lon }
  } catch {
    return null
  }
}

export function saveLocation(loc) {
  try {
    if (loc) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ lat: loc.lat, lon: loc.lon, ts: Date.now() }))
      localStorage.removeItem(OPT_OUT_KEY) // asking for location undoes a prior opt-out
    } else {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.setItem(OPT_OUT_KEY, '1') // clearing IS the opt-out signal
    }
  } catch {
    /* ignore */
  }
  return loc ? { lat: loc.lat, lon: loc.lon } : null
}

export function isLocationOptedOut() {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === '1'
  } catch {
    return false
  }
}

export function setLocationOptOut(value) {
  try {
    if (value) localStorage.setItem(OPT_OUT_KEY, '1')
    else localStorage.removeItem(OPT_OUT_KEY)
  } catch {
    /* ignore */
  }
}

// 'granted' | 'prompt' | 'denied' | 'unknown'. Mirrors the camera check in
// ScannerPage. 'unknown' is the safe default: it takes the same path as
// 'prompt', so an unsupported Permissions API just keeps the old behaviour.
export async function queryLocationPermission() {
  if (!navigator.permissions?.query) return 'unknown'
  try {
    const result = await navigator.permissions.query({ name: 'geolocation' })
    return result.state
  } catch {
    return 'unknown'
  }
}

// Ask the browser for the current position. Resolves { lat, lon } or rejects
// (permission denied / unsupported / timeout).
export function requestLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('geolocation-unsupported'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      (err) => reject(err),
      { timeout: 8000, maximumAge: 5 * 60 * 1000 }
    )
  })
}
